<script lang="ts">
	/**
	 * The link to a credit note's document, wherever a credit note is named (Design §4.6).
	 *
	 * One component for every support screen that names a credit note, for the reason
	 * `InvoiceDocumentLinks` is one component: the rules are small, and a second copy of them is a
	 * second copy that drifts.
	 *
	 * - **It is a fetch, not a navigation.** The endpoint is token-authenticated and the token
	 *   travels in a header, so a plain `<a href>` reaches them signed out and gets a 401 — and the
	 *   alternative, a token in the URL, hands it to every referrer log in between. The object URL
	 *   is deliberately not revoked straight away: Safari cancels a navigation to a blob that has
	 *   already been released, and the browser reclaims it when the tab closes.
	 * - **It reads support's endpoint, always.** This client is the admin console; the customer's
	 *   own copy of this component — which resolves ownership through the caller and so needs the
	 *   customer endpoint — lives in AbstractID with the account pages. There used to be an `admin`
	 *   prop choosing between the two here, and every remaining caller passed `true`.
	 * - **The number is the title**, so somebody looking for one credit note among several can see
	 *   which is which without opening them.
	 */

	import { fetchAdminCreditNoteDocument } from '../AppNetwork';
	import { toastError } from '../stores/toast';

	export let uuid: string;
	export let documentNumber: string;
	/** What the control says. The tables that already have a Document column want just the number. */
	export let label: string = 'Credit note PDF';

	/** Per instance, so one row's spinner does not blank its neighbours. */
	let downloading: boolean = false;

	async function fnOpen(): Promise<void> {
		downloading = true;
		try {
			const blob = await fetchAdminCreditNoteDocument(uuid);
			window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
		} catch (error) {
			toastError('The credit note could not be opened.', (error as Error).message);
		} finally {
			downloading = false;
		}
	}
</script>

<button
	type="button"
	class="text-primary-500 hover:underline disabled:opacity-50"
	title={`Credit note ${documentNumber}`}
	disabled={downloading}
	on:click={() => void fnOpen()}
>
	{downloading ? 'Opening…' : label}
</button>
